import customAxios from "./customAxios";

/**
 * Default timeout for API requests (30 seconds)
 */
const DEFAULT_TIMEOUT = 30000;

/**
 * Fetch surveyors available for the selected week
 * @param {string} baseUrl - API base URL
 * @param {string} startDate - Start date of the week (YYYY-MM-DD)
 * @param {string} endDate - End date of the week (YYYY-MM-DD)
 * @param {string|null} surveyorType - Optional surveyor type filter (1, 2, 3, 4)
 * @returns {Promise<{response: Object, status: number} | {error: Error}>}
 */
export const getSurveyorsForWeek = (baseUrl, startDate, endDate, surveyorType = null) => {
  const path = `${baseUrl}/operation_schedules/surveyors`;
  const params = {
    start_date: startDate,
    end_date: endDate
  };

  if (surveyorType) {
    params.surveyor_type = surveyorType;
  }

  return customAxios.get(path, { params, timeout: DEFAULT_TIMEOUT })
    .then(({ data, status }) => ({ response: data, status }))
    .catch(error => ({ error }));
};

/**
 * Fetch schedule data for a specific surveyor
 * @param {string} baseUrl - API base URL
 * @param {number} surveyorId - Surveyor ID
 * @param {string} startDate - Start date of the week (YYYY-MM-DD)
 * @param {string} endDate - End date of the week (YYYY-MM-DD)
 * @returns {Promise<{response: Object, status: number} | {error: Error}>}
 */
export const getSurveyorSchedule = (baseUrl, surveyorId, startDate, endDate) => {
  const path = `${baseUrl}/operation_schedules/${surveyorId}/schedule`;
  const params = {
    start_date: startDate,
    end_date: endDate
  };

  return customAxios.get(path, { params, timeout: DEFAULT_TIMEOUT })
    .then(({ data, status }) => ({ response: data, status }))
    .catch(error => ({ error }));
};

/**
 * Update primary surveyor for a job service (cross-surveyor reassignment)
 * @param {string} baseUrl - API base URL
 * @param {number} jobServiceId - Job Service ID
 * @param {number} newSurveyorId - New Primary Surveyor ID
 * @returns {Promise<{response: Object, status: number} | {error: Error}>}
 */
export const updatePrimarySurveyor = (baseUrl, jobServiceId, newSurveyorId) => {
  const path = `${baseUrl}/operation_schedules/update_primary_surveyor`;

  return customAxios.put(path, {
    job_service_id: jobServiceId,
    new_surveyor_id: newSurveyorId
  })
    .then(({ data, status }) => ({ response: data, status }))
    .catch(error => ({ error }));
};

/**
 * Update assignment date for a job service (same-surveyor reschedule)
 * @param {string} baseUrl - API base URL
 * @param {number} jobServiceId - Job Service ID
 * @param {number} surveyorId - Surveyor ID
 * @param {string} oldDate - Old assignment date (YYYY-MM-DD)
 * @param {string} newDate - New assignment date (YYYY-MM-DD)
 * @returns {Promise<{response: Object, status: number} | {error: Error}>}
 */
export const updateAssignmentDate = (baseUrl, jobServiceId, surveyorId, oldDate, newDate) => {
  const path = `${baseUrl}/operation_schedules/update_assignment_date`;

  return customAxios.put(path, {
    job_service_id: jobServiceId,
    surveyor_id: surveyorId,
    old_date: oldDate,
    new_date: newDate
  })
    .then(({ data, status }) => ({ response: data, status }))
    .catch(error => ({ error }));
};

/**
 * Get suggested job services for a surveyor on a specific date
 * @param {string} baseUrl - API base URL
 * @param {number} surveyorId - Surveyor ID
 * @param {string} date - Target date (YYYY-MM-DD)
 * @param {number} availableCapacity - Available capacity in hours
 * @param {number|null} targetDollars - Optional daily target dollars
 * @returns {Promise<{response: Object, status: number} | {error: Error}>}
 */
export const getSuggestedJobs = (baseUrl, surveyorId, date, availableCapacity, targetDollars = null) => {
  const path = `${baseUrl}/operation_schedules/${surveyorId}/suggested_jobs`;
  const params = {
    date,
    available_capacity: availableCapacity
  };

  if (targetDollars !== null && targetDollars !== undefined) {
    params.target_dollars = targetDollars;
  }

  return customAxios.get(path, { params, timeout: DEFAULT_TIMEOUT })
    .then(({ data, status }) => ({ response: data, status }))
    .catch(error => ({ error }));
};

/**
 * Assign a suggested job service to a surveyor
 * @param {string} baseUrl - API base URL
 * @param {number} surveyorId - Surveyor ID
 * @param {number} jobServiceId - Job Service ID
 * @param {string} bookingDate - Booking date (YYYY-MM-DD)
 * @param {string} bookingTime - Booking time (HH:MM)
 * @param {boolean} validateCapacity - Whether to validate capacity (default true)
 * @returns {Promise<{response: Object, status: number} | {error: Error}>}
 */
export const assignSuggestedJob = (baseUrl, surveyorId, jobServiceId, bookingDate, bookingTime, validateCapacity = true) => {
  const path = `${baseUrl}/operation_schedules/${surveyorId}/assign_job`;

  return customAxios.post(path, {
    job_service_id: jobServiceId,
    surveyor_id: surveyorId,
    booking_date: bookingDate,
    booking_time: bookingTime,
    validate_capacity: validateCapacity
  })
    .then(({ data, status }) => ({ response: data, status }))
    .catch(error => ({ error }));
};
